/**
 * Behaviour for the signed-in frame, from the kit's primitives.
 *
 * `shell.ts` draws the markup and the app re-renders it as bindings change;
 * the nodes are never replaced, so each primitive is attached once to the
 * wrapper it finds and kept until the frame is replaced (a public page, the
 * not-found page). Menu items are plain `data-href` / `data-action` rows: the
 * menu is portalled out of `#app` while open, where the app's own click
 * listener cannot see it, so selection arrives here through the primitive.
 */

import { attachDropdownMenu, type DropdownMenuController } from '@hydrogen/ui/primitives/dropdown-menu';
import { attachSidebar, type SidebarController } from '@hydrogen/ui/primitives/sidebar';
import { setTheme, type ThemePreference } from 'hydrogen/theme';

export type ChromeActions = {
  navigate: (href: string) => void;
  signOut: () => void;
};

export type Chrome = {
  /** Attach to the frame the last render drew, or refresh the one already attached. */
  sync: (root: Element, path: string) => void;
  dispose: () => void;
};

/** localStorage key for the rail's collapsed state; the primitive also writes shadcn's cookie. */
const SIDEBAR_KEY = 'matrix.sidebar';

/** Where `hydrogen/theme` keeps the viewer's choice; read here so "never chosen" stays distinct from `system`. */
const THEME_KEY = 'hydrogen-theme';

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

/** The viewer's stored theme, or null where none was ever chosen. */
function storedTheme(): ThemePreference | null {
  try {
    const value = globalThis.localStorage?.getItem(THEME_KEY);
    return isThemePreference(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Dark is the product's theme and the public pages are drawn for it, so the
 * document is pinned dark without writing a preference. The signed-in frame
 * honours a choice made in the user menu.
 */
export function pinDarkTheme(): void {
  const root = document.documentElement;
  root.dataset['theme'] = 'dark';
  root.classList.add('dark');
}

function applyStoredTheme(): void {
  const chosen = storedTheme();
  if (chosen === null) pinDarkTheme();
  else setTheme(chosen);
}

/** Mark the theme radio items for the choice in force: never chosen reads as Dark. */
function syncThemeItems(root: ParentNode): void {
  const current = storedTheme() ?? 'dark';
  for (const item of root.querySelectorAll<HTMLElement>('[data-group="theme"] [data-slot="dropdown-menu-radio-item"]')) {
    const checked = item.dataset['value'] === current;
    item.setAttribute('aria-checked', String(checked));
    item.dataset['state'] = checked ? 'checked' : 'unchecked';
    item.querySelector<HTMLElement>('[data-menu-item-indicator]')?.toggleAttribute('hidden', !checked);
  }
}

export function createChrome(actions: ChromeActions): Chrome {
  let wrapper: HTMLElement | null = null;
  let sidebar: SidebarController | null = null;
  let menus: DropdownMenuController[] = [];
  let lastPath: string | null = null;

  const release = (): void => {
    for (const menu of menus) menu.destroy();
    menus = [];
    sidebar?.destroy();
    sidebar = null;
    wrapper = null;
  };

  /**
   * shadcn's NavUser: beside the rail on a desktop, below the trigger in the
   * mobile sheet, where the side has no room. The primitive reads placement
   * after reporting the open, so it is set as the open is reported.
   */
  const placeUserMenu = (root: HTMLElement): void => {
    if (root.dataset['menu'] !== 'user') return;
    root
      .querySelector<HTMLElement>('[data-slot="dropdown-menu-content"]')
      ?.setAttribute('data-prefer-side', sidebar?.isMobile ? 'bottom' : 'right');
  };

  const attach = (next: HTMLElement): void => {
    release();
    wrapper = next;
    applyStoredTheme();
    syncThemeItems(next);
    sidebar = attachSidebar(next, { collapsible: 'icon', storageKey: SIDEBAR_KEY, mobileLabel: 'Navigation' });
    for (const root of next.querySelectorAll<HTMLElement>('[data-slot="dropdown-menu"]')) {
      menus.push(
        attachDropdownMenu(root, {
          onOpenChange: (open) => {
            if (open) placeUserMenu(root);
          },
          onSelect: ({ item }) => {
            const { href, action } = item.dataset;
            if (href !== undefined) actions.navigate(href);
            else if (action === 'signout') actions.signOut();
          },
          onValueChange: (group, value) => {
            if (group.dataset['group'] !== 'theme' || !isThemePreference(value)) return;
            setTheme(value);
            syncThemeItems(group);
          },
        }),
      );
    }
  };

  return {
    sync(root, path) {
      const found = root.querySelector<HTMLElement>('[data-slot="sidebar-wrapper"]');
      if (found === null) {
        if (wrapper !== null) {
          release();
          pinDarkTheme();
        }
        lastPath = null;
        return;
      }
      if (found !== wrapper) attach(found);
      else sidebar?.refresh();
      // A destination chosen from the mobile sheet closes it.
      if (lastPath !== null && lastPath !== path && sidebar?.openMobile) sidebar.setOpenMobile(false);
      lastPath = path;
    },
    dispose() {
      release();
      lastPath = null;
    },
  };
}
